import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  useGetOrionFlowTypesQuery,
  useSetOrionFlowTypesMutation,
} from '../../app/api';

/* Super Admin → Config → Flow types.
 *
 * The definition of net new AUM, as a page. Which Orion transaction types count
 * as client money IS that definition, and until 2026-09-09 it lived in two
 * places with different rules: two `configurables` rows with no UI at all, and a
 * hardcoded dict in `integrations/orion_sync.py` that held the excluded types
 * and their reasons. Half the definition could only be changed by a developer.
 *
 * What made that untenable was a measurement rather than an opinion. Type 39
 * "Merge In from Other Account" is excluded because a merge is usually an
 * account being restructured — but on the Lepre household all 30 merge rows are
 * unpaired ACAT receipts, a new client's $23.5M book arriving from another firm,
 * reported as $0 net new. Deciding that is a business call.
 *
 * THE FOUR TREATMENTS ARE NOT THREE. `excluded` and `ignore` both mean "not in
 * the total", and the difference between them is the one thing this page has to
 * teach: an excluded type is still FETCHED and STORED, which is what puts it in
 * the amber columns of the household breakdown and makes it arguable. An ignored
 * type is never fetched, so it exists nowhere in the product. Moving a type from
 * ignore to excluded is how you start collecting the evidence to judge it — the
 * move that was made by hand for the merge types on 2026-08-28.
 */

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  canvas:    '#f3f5f8',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const TREATMENTS = [
  {
    key: 'inflow',
    label: 'Counted — inflow',
    fg: '#0069a6',
    bg: '#e8f3fb',
    help: 'Money in. Adds to the headline net new figure.',
  },
  {
    key: 'outflow',
    label: 'Counted — outflow',
    fg: '#0069a6',
    bg: '#e8f3fb',
    help: 'Money out. Subtracts from the headline net new figure.',
  },
  {
    key: 'excluded',
    label: 'Excluded',
    fg: '#8a6412',
    bg: '#fdf4e3',
    help: 'Fetched and stored but not counted. Shows in the amber columns on the '
        + 'household breakdown, so the exclusion is priced and can be argued with. '
        + 'Give it a reason — that reason is stamped on every row and shown to advisors.',
  },
  {
    key: 'external_only',
    label: 'Counted when external',
    fg: '#0069a6',
    bg: '#e8f3fb',
    help: 'For merges. Counted per TRANSACTION, not per type: a row counts only when '
        + 'its counterparty account is not one of ours — no mirror merge-out anywhere at '
        + 'PCA, and a custodian code (TOA/DRC/TCU) meaning it transferred in from another '
        + 'firm. Journal-coded and undecidable rows stay excluded. This is the treatment '
        + 'that answers "a new client’s book arrived as a merge".',
  },
  {
    key: 'new_client_only',
    label: 'Counted for new clients',
    fg: '#0069a6',
    bg: '#e8f3fb',
    help: 'For Starting Value. Counted per TRANSACTION, not per type: a row counts only when '
        + 'its household had no earlier activity in Orion, so it is a new client arriving. '
        + 'An existing client opening another account stays excluded, and so does anything '
        + 'in the first month of stored history, where every household looks new.',
  },
  {
    key: 'ignore',
    label: 'Never fetched',
    fg: '#8a3b3b',
    bg: '#fbeceb',
    help: 'Not requested from Orion at all, so it exists nowhere in our database. '
        + 'Cheapest option, and the one that hides things: a type here can only be '
        + 'seen in the live household drill-down.',
  },
];

const byKey = Object.fromEntries(TREATMENTS.map((t) => [t.key, t]));

function money(n) {
  const v = Number(n) || 0;
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function OrionFlowTypes() {
  const { data, isLoading, error, refetch } = useGetOrionFlowTypesQuery();
  const [save, { isLoading: isSaving }] = useSetOrionFlowTypesMutation();

  // Edits are held locally until Save, keyed by type id. A treatment change
  // reclassifies real client money, so it is a deliberate submit rather than a
  // dropdown that writes as it changes.
  const [edits, setEdits] = useState({});
  const [result, setResult] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [adding, setAdding] = useState(false);

  const rows = useMemo(() => {
    const base = data?.types || [];
    return base.map((r) => ({ ...r, ...(edits[r.trans_type_id] || {}) }));
  }, [data, edits]);

  const dirty = Object.keys(edits);
  const newTypes = rows.filter((r) => r._isNew);

  const change = (id, patch) => {
    setResult(null);
    setSaveError(null);
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const revert = () => {
    setEdits({});
    setResult(null);
    setSaveError(null);
  };

  const onSave = async () => {
    setSaveError(null);
    const updates = rows
      .filter((r) => dirty.includes(String(r.trans_type_id)))
      .map((r) => ({
        trans_type_id: Number(r.trans_type_id),
        treatment: r.treatment,
        reason: r.reason || null,
        trans_type_name: r.trans_type_name || null,
      }));
    try {
      const res = await save({ updates }).unwrap();
      setResult(res);
      setEdits({});
      refetch();
    } catch (e) {
      setSaveError(e?.data?.detail || 'Could not save the changes.');
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '1.5rem', color: brand.muted }}>
        <span className="spinner-border spinner-border-sm me-2" />Loading flow types…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '1.5rem', color: brand.red }}>
        {error?.data?.detail || 'Could not load the flow types.'}
      </div>
    );
  }

  const cell = { padding: '0.4rem 0.5rem', verticalAlign: 'top' };
  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

  return (
    <div style={{ padding: '1rem 1.25rem', maxWidth: 1250 }}>
      <h5 style={{ color: brand.deepBlue, fontWeight: 600, marginBottom: '0.25rem' }}>
        Net New AUM — flow types
      </h5>
      <p style={{ color: brand.muted, fontSize: '0.82rem', maxWidth: 820 }}>
        Which Orion transaction types count as client money. This <em>is</em> the
        definition of net new AUM — the Work → Net New AUM page reports whatever is
        set here. Saving re-applies the definition to the transactions already stored
        and rebuilds the monthly rollup, which takes seconds and does not re-read Orion.
      </p>

      <Legend />

      {result && (
        <div style={{
          background: '#eaf6ef', border: `1px solid ${brand.green}`, borderRadius: 4,
          padding: '0.6rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.82rem',
        }}>
          <strong style={{ color: brand.green }}>Saved.</strong>{' '}
          {result.reclassified && (
            <>
              Reclassified{' '}
              {Object.entries(result.reclassified)
                .map(([k, v]) => `${v.n.toLocaleString()} ${k}`)
                .join(', ')}
              .{' '}
            </>
          )}
          {result.rollup && (
            <>
              Rollup rebuilt: {result.rollup.household_months.toLocaleString()} household-months,
              {' '}{money(result.rollup.net_flow)} net.{' '}
            </>
          )}
          {result.needs_sync?.length > 0 && (
            <div style={{ marginTop: '0.4rem', color: '#8a6412' }}>
              <i className="bi bi-exclamation-triangle-fill me-1" />
              Type{result.needs_sync.length === 1 ? '' : 's'}{' '}
              <strong>{result.needs_sync.join(', ')}</strong>{' '}
              {result.needs_sync.length === 1 ? 'was' : 'were'} never fetched before, so
              nothing is stored for {result.needs_sync.length === 1 ? 'it' : 'them'} yet.
              Run a live sync from{' '}
              <a href="/admin/orion-sync">Super Admin → Orion Sync</a> to pull{' '}
              {result.needs_sync.length === 1 ? 'its' : 'their'} history before the
              numbers will move.
            </div>
          )}
        </div>
      )}
      {saveError && (
        <div style={{
          background: '#fbeceb', border: `1px solid ${brand.red}`, borderRadius: 4,
          padding: '0.6rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.82rem',
          color: brand.red,
        }}>
          {saveError}
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem',
      }}>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!dirty.length || isSaving}
          onClick={onSave}
        >
          {isSaving && <span className="spinner-border spinner-border-sm me-2" />}
          Save {dirty.length ? `${dirty.length} change${dirty.length === 1 ? '' : 's'}` : 'changes'}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={!dirty.length || isSaving}
          onClick={revert}
        >
          Discard
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => setAdding(true)}>
          <i className="bi bi-plus-lg me-1" />Add a type Orion has that we don&apos;t
        </button>
      </div>

      {adding && (
        <AddType
          existing={rows.map((r) => Number(r.trans_type_id))}
          onCancel={() => setAdding(false)}
          onAdd={(id) => {
            setAdding(false);
            // Held as a local edit like any other, so it is written by the same
            // Save and can be discarded without touching the table.
            setEdits((prev) => ({
              ...prev,
              [id]: { trans_type_id: id, treatment: 'ignore', reason: '', _isNew: true },
            }));
          }}
        />
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ color: brand.muted, textAlign: 'left',
                       borderBottom: `2px solid ${brand.hairline}` }}>
            <th style={cell}>Type</th>
            <th style={{ ...cell, width: 170 }}>Treatment</th>
            <th style={cell}>Reason shown to advisors</th>
            <th style={num}>Stored</th>
            <th style={num}>Total</th>
            <th style={cell}>Last changed</th>
          </tr>
        </thead>
        <tbody>
          {newTypes.map((r) => (
            <TypeRow key={`new-${r.trans_type_id}`} row={r} isNew
                     onChange={(patch) => change(r.trans_type_id, patch)}
                     dirty cell={cell} num={num} />
          ))}
          {rows.filter((r) => !r._isNew).map((r) => (
            <TypeRow key={r.trans_type_id} row={r}
                     onChange={(patch) => change(r.trans_type_id, patch)}
                     dirty={dirty.includes(String(r.trans_type_id))}
                     cell={cell} num={num} />
          ))}
        </tbody>
      </table>

      <p style={{ color: brand.muted, fontSize: '0.75rem', marginTop: '0.8rem' }}>
        <strong>Stored</strong> and <strong>Total</strong> are what is in
        {' '}<code>orion_transactions</code> today, over whatever window the last sync
        covered — a type set to <em>never fetched</em> reads zero because it has never
        been pulled, not because it never happened. To find out what an unfetched type
        is actually worth, open a household from Work → Net New AUM: that drill-down
        reads Orion live and shows every type, including these.
      </p>
    </div>
  );
}

function TypeRow({ row, onChange, dirty, isNew, cell, num }) {
  const t = byKey[row.treatment] || {};
  // An excluded type with no reason is the one combination worth nagging about:
  // that string is stamped onto every row and is what an advisor reads when they
  // ask why their household shows $0.
  const missingReason = row.treatment === 'excluded' && !(row.reason || '').trim();
  return (
    <tr style={{
      borderBottom: `1px solid ${brand.hairline}`,
      background: dirty ? '#fffbe9' : 'transparent',
    }}>
      <td style={cell}>
        <strong>{row.trans_type_id}</strong>{' '}
        {isNew ? (
          <input
            type="text"
            className="form-control form-control-sm d-inline-block"
            style={{ width: 190, fontSize: '0.78rem' }}
            placeholder="Orion's name for it"
            value={row.trans_type_name || ''}
            onChange={(e) => onChange({ trans_type_name: e.target.value })}
          />
        ) : (
          row.trans_type_name || <span style={{ color: brand.muted }}>unnamed</span>
        )}
        {isNew && (
          <span style={{ marginLeft: 5, color: brand.tealBlue, fontSize: '0.7rem',
                         fontWeight: 700 }}>
            new
          </span>
        )}
      </td>
      <td style={cell}>
        <select
          className="form-select form-select-sm"
          style={{ fontSize: '0.78rem', color: t.fg, background: t.bg, fontWeight: 600 }}
          value={row.treatment}
          onChange={(e) => onChange({ treatment: e.target.value })}
        >
          {TREATMENTS.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </td>
      <td style={cell}>
        <textarea
          className="form-control form-control-sm"
          style={{
            fontSize: '0.75rem', minHeight: 34,
            borderColor: missingReason ? brand.goldenrod : undefined,
          }}
          rows={1}
          placeholder={row.treatment === 'excluded'
            ? 'Why is this not client money? Advisors read this.'
            : 'Optional note'}
          value={row.reason || ''}
          onChange={(e) => onChange({ reason: e.target.value })}
        />
        {missingReason && (
          <div style={{ color: '#8a6412', fontSize: '0.7rem', marginTop: 2 }}>
            Excluded with no reason — the household breakdown will show a blank
            explanation.
          </div>
        )}
      </td>
      <td style={num}>{(row.transactions || 0).toLocaleString()}</td>
      <td style={{ ...num, fontWeight: row.total ? 600 : 400,
                   color: row.total ? undefined : brand.muted }}>
        {row.total ? money(row.total) : '—'}
      </td>
      <td style={{ ...cell, color: brand.muted, fontSize: '0.73rem' }}>
        {row.updated_by
          ? <>{row.updated_by}<br />{String(row.updated || '').slice(0, 10)}</>
          : 'original setup'}
      </td>
    </tr>
  );
}
TypeRow.propTypes = {
  row: PropTypes.shape({
    trans_type_id: PropTypes.number,
    trans_type_name: PropTypes.string,
    treatment: PropTypes.string,
    reason: PropTypes.string,
    transactions: PropTypes.number,
    total: PropTypes.number,
    updated_by: PropTypes.string,
    updated: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  dirty: PropTypes.bool,
  isNew: PropTypes.bool,
  cell: PropTypes.object.isRequired,
  num: PropTypes.object.isRequired,
};

function Legend() {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
      gap: '0.5rem', marginBottom: '0.9rem',
    }}>
      {TREATMENTS.map((t) => (
        <div key={t.key} style={{
          border: `1px solid ${brand.hairline}`, borderRadius: 4, padding: '0.5rem 0.6rem',
          background: '#fff',
        }}>
          <span style={{
            padding: '0.05rem 0.35rem', borderRadius: 3, fontSize: '0.68rem',
            fontWeight: 700, color: t.fg, background: t.bg,
          }}>
            {t.label}
          </span>
          <div style={{ color: brand.muted, fontSize: '0.73rem', marginTop: 4 }}>
            {t.help}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Adding a type by id, for the ones Orion has and this table does not.
 *
 * The sync deliberately never creates rows here — discovering a type has to be a
 * decision somebody records, not a side effect of a nightly job — so this is how
 * a type first gets onto the page. Ids come from the household drill-down, which
 * reads Orion live and labels anything unknown. */
function AddType({ existing, onCancel, onAdd }) {
  const [value, setValue] = useState('');
  const id = Number(value);
  const valid = Number.isInteger(id) && id > 0 && !existing.includes(id);
  const duplicate = Number.isInteger(id) && id > 0 && existing.includes(id);

  return (
    <div style={{
      border: `1px solid ${brand.hairline}`, borderRadius: 4, background: brand.canvas,
      padding: '0.6rem 0.8rem', marginBottom: '0.6rem',
      display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem',
    }}>
      <span>Orion transaction type id</span>
      <input
        type="number"
        className="form-control form-control-sm"
        style={{ width: 110 }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="button" className="btn btn-sm btn-primary"
              disabled={!valid} onClick={() => onAdd(id)}>
        Add
      </button>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
        Cancel
      </button>
      {duplicate && (
        <span style={{ color: brand.goldenrod }}>Type {id} is already listed.</span>
      )}
      <span style={{ color: brand.muted, marginLeft: 'auto' }}>
        Ids for types we don&apos;t hold come from the household drill-down.
      </span>
    </div>
  );
}
AddType.propTypes = {
  existing: PropTypes.arrayOf(PropTypes.number).isRequired,
  onCancel: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};
